/**
 * publishedRenderSelection.ts
 *
 * Pure selectors over a Video record's `PublishRecord`s, answering "which
 * render should play for this language". Every language of one aspect ratio
 * renders into the SAME Video record and only the canonical (original-language)
 * cell mirrors its output to `videoFilePrimary` — so a translated render is
 * reachable only through its own record, and a clip published solely in a
 * translation reads as unpublished to anything that checks the legacy field
 * (PLAT-11548).
 *
 * No IO, no MobX: the caller fetches the records and resolves the chosen
 * record's signed URL. "This language has no render" is an ordinary answer, not
 * a failure, so these return `null` rather than a `Result` (same shape as
 * `resolveStoryPlaybackSource`).
 */

import { PublishStatus } from '@src/types.ts/publishRecord';
import { VideoResolution } from '@src/types.ts/video';

import type { PublishRecord } from '@src/types.ts/publishRecord';

/**
 * Ranks resolutions highest-first. Third copy of this table, and the three must
 * agree: `PublishFanoutService.expandCells` uses it to pick the canonical cell
 * (original language × AR × highest resolution) whose render becomes
 * `videoFilePrimary`, and `arbor-v1 lib/publishRecordResources.ts` uses it to
 * resolve a share link's translated render. A translated render only plays the
 * equivalent of the canonical one if all three rank resolutions the same way.
 */
const RESOLUTION_RANK: Record<string, number> = {
  [VideoResolution.HD]: 4,
  [VideoResolution.High]: 3,
  [VideoResolution.Medium]: 2,
  [VideoResolution.Low]: 1,
  [VideoResolution.Original]: 0,
};

/**
 * A record that can actually be played: succeeded, non-canonical, and carrying
 * a rendered artifact. Canonical records are excluded because the Video record
 * already owns that render *and* Mux has processed it — playback falls through
 * to `videoFilePrimary` so the original language keeps adaptive streaming.
 *
 * Exported for `clipCardPublishState`, which asks the same question of the
 * clip cards' grid (PLAT-11591). The card and the preview must agree on which
 * renders exist, or a card reads published and its preview disagrees.
 */
export function isPlayableTranslatedRecord(record: PublishRecord): boolean {
  return (
    record.publishStatus === PublishStatus.Succeeded &&
    !record.mirrorsToLegacy &&
    !!record.outputJson?.video?.fileKey
  );
}

/**
 * Pick the record to play out of one language's publishes: the newest publish's
 * highest resolution.
 *
 * Recency alone cannot name a record — one fan-out writes every selected
 * resolution for a cell within milliseconds, so "newest record" among them is
 * just whichever order the resolution loop happened to run in. Scope to the
 * newest publish (its shared `groupId`), then rank resolution the way
 * `expandCells` does when it picks the canonical cell.
 *
 * `records` must be newest-first (as `useClipPublishRecords` returns them).
 */
export function selectTranslatedRender(
  records: PublishRecord[],
  language: string | null | undefined,
): PublishRecord | null {
  if (!language) return null;

  const candidates = records.filter(
    record =>
      record.language === language && isPlayableTranslatedRecord(record),
  );
  return pickNewestBestResolution(candidates);
}

/**
 * The record behind an explicit pick from the Downloads list, or `null` when the
 * pick is stale (record gone, never succeeded, artifact missing) so the caller
 * can fall back to the automatic rule.
 *
 * Deliberately does NOT exclude canonical records the way the automatic rules
 * do. Those defer to `videoFilePrimary` because it holds the same bytes over
 * Mux — but only for the *newest* canonical publish. Picking an earlier
 * original-language row means asking for bytes the legacy field no longer
 * points at, so a pinned record always plays its own artifact.
 */
export function selectPinnedRender(
  records: PublishRecord[],
  recordId: string | null | undefined,
): PublishRecord | null {
  if (!recordId) return null;
  const pinned = records.find(record => record.id === recordId);
  if (!pinned) return null;
  return pinned.publishStatus === PublishStatus.Succeeded &&
    pinned.outputJson?.video?.fileKey
    ? pinned
    : null;
}

/**
 * The newest playable translated render for this Video record, in any language
 * — what "this clip has a published version" means once the legacy field can't
 * answer it, and the fallback when the selected language has no render of its
 * own. Same newest-publish/highest-resolution rule as above, so the two agree
 * whenever the newest publish happens to be the selected language.
 */
export function newestTranslatedRender(
  records: PublishRecord[],
): PublishRecord | null {
  return pickNewestBestResolution(records.filter(isPlayableTranslatedRecord));
}

/**
 * Scope newest-first `candidates` to the newest publish and return its highest
 * resolution. Records written before `group_id` existed have none; with no group
 * to scope to the single newest record stands, rather than every ungrouped
 * record merging into one pseudo-group.
 *
 * Exported for `clipCardPublishState` (PLAT-11591) so the render a card names
 * is the one its preview plays — see `isPlayableTranslatedRecord`.
 */
export function pickNewestBestResolution(
  candidates: PublishRecord[],
): PublishRecord | null {
  const newest = candidates[0];
  if (!newest) return null;

  const newestPublish = newest.groupId
    ? candidates.filter(record => record.groupId === newest.groupId)
    : [newest];

  // Stable sort keeps the newest-first order for equal ranks. An unrecognised
  // resolution ranks below every known one but stays usable.
  return [...newestPublish].sort(
    (a, b) =>
      (RESOLUTION_RANK[b.resolution] ?? -1) -
      (RESOLUTION_RANK[a.resolution] ?? -1),
  )[0];
}
